import {
  Chip, Skeleton, TableCell, TableRow, Tooltip
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { Typography } from 'bso-ui';
import API from 'common/api';
import { timestampToDate } from 'helpers/formatters';
import { get } from 'helpers/query/fetchers';
import useList from 'hooks/useList';
import React from 'react';

const TooltipChip = ({
  timeStamp, label, color, url
}) => (
  <Tooltip title={timestampToDate(timeStamp, 'yyyy-MM-DD, HH:mm:ss')}>
    <Chip
      label={label}
      color={color}
      clickable
      component="a"
      href={url}
      target="_blank"
      style={{ textDecoration: 'underline', ...(color === 'error' && { animation: 'blinker 1s linear infinite' }) }}
    />
  </Tooltip>
);

const RadioDeviceRow = ({
  deviceName = ''
}) => {
  // get device, then device.site, then site.weather
  const deviceQuery = useList(`device-${deviceName}`, `${API.DEVICE}?hostname=${deviceName}`);
  const device = deviceQuery[0]?.data?.results?.[0];

  const siteQuery = useList(
    `site-${deviceName}`,
    device?.site ? API.SITE_DYNAMIC(device.site) : null,
    null,
    {
      enabled: !!device?.site
    }
  );

  const getWeather = (data) => {
    let link = null;
    let icon = null;
    let condition = null;

    link = data?.cctv || null;
    icon = `http:${data?.weather?.icon}`;
    condition = data?.weather?.weather_condition;
    if (icon) {
      return (
        <p>
          <a href={link} title={condition} target="_blank" rel="noreferrer" aria-label="weather">
            <img alt="" src={icon} style={{ height: '50px' }} />
          </a>
        </p>
      );
    }
    return <p>---</p>;
  };

  const checkTypes = ['check-rf-ping', 'check-rf-eband-rssidbm',
    'check-rf-eband-ber', 'check-rf-eband-temperature',
    'check-rf-eband-tribinput', 'check-rf-eband-txpowerdbm'];

  // eslint-disable-next-line max-len
  const getApiEndpoint = (checkType) => `${API.MONITORING_RESULTS}/${deviceName}/${checkType}-${deviceName.toLowerCase().replaceAll('.', '-')}/`;

  const deviceCheckQueries = useQueries({
    queries: checkTypes.map((checkType) => ({
      queryKey: [`${deviceName}-${checkType}`],
      queryFn: () => get(getApiEndpoint(checkType)).then((res) => res.data),
      refetchOnWindowFocus: false,
      refetchInterval: 60000
    })),
  });

  // eslint-disable-next-line max-len
  const getUchiwaUrl = (hostName, checkName) => `https://monitoring.cympa.io/#/client/RF-01/${hostName}?check=check-rf-${checkName}-${hostName.toLowerCase().replaceAll('.', '-')}`;

  const getPing = (data) => {
    const status = data.check?.status;
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'ping');
    let label = '';
    let color = '';
    if (status === 0) {
      label = 'Up';
      color = 'success';
    } else if ([1, 3].includes(status)) {
      label = 'Check it and tell us';
      color = 'warning';
    } else {
      label = 'Down';
      color = 'error';
    }
    return (
      <TooltipChip
        label={label}
        color={color}
        timeStamp={timeStamp}
        url={url}
        style={color === 'error' ? { animation: 'blinker 1s linear infinite' } : {}}
      />
    );
  };

  const getRssidbm = (data) => {
    const value = Math.abs(parseInt(data.check?.output?.replace('RSSIDBM: ', ''), 10));
    const warning = Math.abs(parseInt(data.check?.device_thresholds?.rssidbm?.warning, 10));
    const critical = Math.abs(parseInt(data.check?.device_thresholds?.rssidbm?.critical, 10));
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'rssidbm');
    let label = '';
    let color = '';
    if (value <= warning) {
      label = 'Ok';
      color = 'success';
    } else if (value > warning && value <= critical) {
      label = 'Warn';
      color = 'warning';
    } else if (value > critical) {
      label = 'Crit';
      color = 'error';
    }
    label += ` (${parseInt(data.check?.output?.replace('RSSIDBM: ', ''), 10)})`;
    return (
      <TooltipChip label={label} color={color} timeStamp={timeStamp} url={url} />
    );
  };

  const getBer = (data) => {
    const value = parseInt(data.check?.output?.replace('BER: ', ''), 10);
    const warning = data.check?.device_thresholds?.ber?.warning;
    const critical = data.check?.device_thresholds?.ber?.critical;
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'ber');
    let label = '';
    let color = '';
    if (value <= warning) {
      label = 'Ok';
      color = 'success';
    } else if (value > warning && value <= critical) {
      label = 'Warn';
      color = 'warning';
    } else if (value > critical) {
      label = 'Crit';
      color = 'error';
    }
    label += ` (${value})`;
    return (
      <TooltipChip label={label} color={color} timeStamp={timeStamp} url={url} />
    );
  };

  const getTemperature = (data) => {
    const value = parseInt(data.check?.output?.replace('TEMPERATURE: ', ''), 10);
    const warning = data.check?.device_thresholds?.temperature?.warning;
    const critical = data.check?.device_thresholds?.temperature?.critical;
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'temperature');
    let label = '';
    let color = '';
    if (value <= warning) {
      label = 'Ok';
      color = 'success';
    } else if (value > warning && value <= critical) {
      label = 'Warn';
      color = 'warning';
    } else if (value > critical) {
      label = 'Crit';
      color = 'error';
    }
    label += ` (${value})`;
    return (
      <TooltipChip label={label} color={color} timeStamp={timeStamp} url={url} />
    );
  };

  const getTribinput = (data) => {
    const status = data.check?.status;
    const value = parseInt(data.check?.output?.replace('TRIBINPUT: ', ''), 10);
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'tribinput');
    let label = '';
    let color = '';
    if (status === 0) {
      label = 'Ok';
      color = 'success';
    } else if (status === 1) {
      label = 'Warn';
      color = 'warning';
    } else if (status === 2) {
      label = 'Crit';
      color = 'warning';
    } else {
      label = 'Check it and tell us';
      color = 'warning';
    }
    label += ` (${value})`;

    return (
      <TooltipChip label={label} color={color} timeStamp={timeStamp} url={url} />
    );
  };

  const getTxpowerdbm = (data) => {
    const status = data.check?.status;
    const value = parseInt(data.check?.output?.replace('TXPOWERDBM: ', ''), 10);
    const timeStamp = data.check?.executed;
    const url = getUchiwaUrl(data.check?.link, 'txpowerdbm');
    let label = '';
    let color = '';
    if (status === 0) {
      label = 'Ok';
      color = 'success';
    } else if (status === 1) {
      label = 'Warn';
      color = 'warning';
    } else if (status === 2) {
      label = 'Crit';
      color = 'warning';
    } else {
      label = 'Check it and tell us';
      color = 'warning';
    }
    label += ` (${value})`;

    return (
      <TooltipChip label={label} color={color} timeStamp={timeStamp} url={url} />
    );
  };

  const renderData = (query, method) => (
    <TableCell>
      {query.isError && 'Not monitored'}
      {query.isLoading && <Skeleton />}
      {query.data && method(query.data)}
    </TableCell>
  );

  return (
    <TableRow
      hover
      key={deviceName}
    >
      <TableCell>
        <Typography variant="body2">
          {deviceName}
        </Typography>
      </TableCell>
      {renderData(deviceCheckQueries[0], getPing)}
      {renderData(deviceCheckQueries[1], getRssidbm)}
      {renderData(deviceCheckQueries[2], getBer)}
      {renderData(deviceCheckQueries[3], getTemperature)}
      {renderData(deviceCheckQueries[4], getTribinput)}
      {renderData(deviceCheckQueries[5], getTxpowerdbm)}
      {renderData(siteQuery[0], getWeather)}
    </TableRow>
  );
};

export default RadioDeviceRow;
